import { Component, signal, OnInit, Inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CommonModule, DOCUMENT } from '@angular/common';
import { SidebarComponent } from './components/sidebar/sidebar';
import { AuthService } from './services/auth.service';
import { SessionExpiredService } from './services/session-expired.service';
import { TranslateService } from '@ngx-translate/core';
import itTranslations from '../assets/i18n/it.json';
import enTranslations from '../assets/i18n/en.json';
import { ChangePasswordModalComponent } from './components/change-password-modal/change-password-modal.component';
import { SessionExpiredOverlayComponent } from './components/session-expired-overlay/session-expired-overlay.component';
import { NotificationService } from './services/notification.service';
import { VersionCheckService } from './services/version-check.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, SidebarComponent, CommonModule, ChangePasswordModalComponent, SessionExpiredOverlayComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  protected readonly title = signal('audience-engine');
  isAuthenticated = false;
  private isPublicPage = false;
  showChangePasswordModal = false;
  sessionExpired$!: SessionExpiredService['expired$'];

  private readonly supportedLanguages: Array<'en' | 'it'> = ['it', 'en'];

  constructor(
    private authService: AuthService,
    private router: Router,
    private translateService: TranslateService,
    private sessionExpiredService: SessionExpiredService,
    public notificationService: NotificationService,
    private versionCheckService: VersionCheckService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.sessionExpired$ = sessionExpiredService.expired$;
    this.initializeLanguage();

    // Initialize authentication state immediately to avoid sidebar flash
    this.isAuthenticated = !!this.authService.currentUserValue;
    this.isPublicPage = this.resolveIsPublicPage();
  }

  ngOnInit(): void {
    this.versionCheckService.start();

    // Lo stato "pagina pubblica" viene ricalcolato a ogni navigazione conclusa, non letto a ogni
    // change detection: cosi' non dipende dal momento in cui il router aggiorna la sua url.
    this.isPublicPage = this.resolveIsPublicPage();
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        this.isPublicPage = this.resolveIsPublicPage();
      });

    // Subscribe to authentication state changes
    this.authService.currentUser$.subscribe(user => {
      this.isAuthenticated = !!user;
      if (!user) {
        this.showChangePasswordModal = false;
      }

      // Redirect to login if not authenticated, unless we are on a public route
      if (!user && !this.isOnPublicPage()) {
        this.rememberInitialDeepLink();
        this.router.navigate(['/login']);
      }
    });
  }

  /**
   * Rotte pubbliche (`data.publicPage`): nessun layout autenticato e nessun redirect al login.
   * `/password-reset` e' pubblica perche' ci arriva chiunque riceva il link via email, anche chi non
   * ha accesso alla Console; se e' gia' loggato la pagina va comunque mostrata senza sidebar.
   */
  private static readonly PUBLIC_ROUTE_PATHS = ['/login', '/password-recovery', '/password-reset'];

  /**
   * Al primo avvio senza sessione questa navigazione su /login parte prima che la navigazione
   * iniziale arrivi all'AuthGuard, che quindi non scrive mai `redirectUrl` e il deep link si perde.
   * Lo salviamo qui, solo per la navigazione iniziale: un logout a meta' sessione non deve
   * riportare il prossimo login sulla pagina lasciata. La validazione la fa LoginComponent.
   */
  private rememberInitialDeepLink(): void {
    if (this.router.navigated) {
      return;
    }
    const location = this.document?.location;
    const target = location ? `${location.pathname}${location.search}${location.hash}` : '';
    if (!target || target === '/') {
      return;
    }
    try {
      localStorage.setItem('redirectUrl', target);
    } catch {
      // Storage bloccato: si atterra sulla home come prima.
    }
  }

  isOnPublicPage(): boolean {
    return this.isPublicPage;
  }

  /** Legge `data.publicPage` sul ramo di rotte attivo. */
  private resolveIsPublicPage(): boolean {
    let route: ActivatedRouteSnapshot | undefined = this.router.routerState.snapshot.root;
    let found = false;
    while (route) {
      if (route.data?.['publicPage'] === true) {
        found = true;
        break;
      }
      route = route.firstChild ?? undefined;
    }
    if (found) {
      return true;
    }
    // Prima che la navigazione iniziale sia completata lo snapshot e' ancora vuoto e `router.url`
    // vale '/': in quel momento il path reale lo conosce solo il browser.
    if (this.router.url === '/') {
      const path = (this.document?.location?.pathname || '').split('?')[0].split('#')[0];
      return App.PUBLIC_ROUTE_PATHS.some(route => path === route || path.startsWith(`${route}/`));
    }
    return false;
  }

  /**
   * Check if current route is home page
   */
  isOnHomePage(): boolean {
    const url = this.router.url;
    return url === '/home' || url === '/';
  }

  /**
   * Check if sidebar should be visible
   */
  get shouldShowSidebar(): boolean {
    return this.isAuthenticated && !this.isOnPublicPage();
  }

  private initializeLanguage(): void {
    this.translateService.addLangs(this.supportedLanguages);
    this.translateService.setDefaultLang('en');
    this.translateService.setTranslation('it', itTranslations, true);
    this.translateService.setTranslation('en', enTranslations, true);

    const browserLang = this.getBrowserLanguage();
    const activeLang = this.supportedLanguages.includes(browserLang) ? browserLang : 'en';

    this.translateService.use(activeLang);
    this.setDocumentLanguage(activeLang);
  }

  private getBrowserLanguage(): 'it' | 'en' {
    if (typeof navigator === 'undefined' || !navigator.language) {
      return 'en';
    }
    const langCode = navigator.language.split('-')[0].toLowerCase();
    return langCode === 'it' ? 'it' : 'en';
  }

  private setDocumentLanguage(lang: string): void {
    if (this.document?.documentElement) {
      this.document.documentElement.lang = lang;
    }
  }

  openChangePasswordModal(): void {
    this.showChangePasswordModal = true;
  }

  closeChangePasswordModal(): void {
    this.showChangePasswordModal = false;
  }
}
